import React, { FC, useCallback, useState, useMemo } from 'react';
import styles from './index.module.scss';
import { Skeleton } from 'src/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faArrowsFromDottedLine, faArrowsToDottedLine } from '@fortawesome/pro-light-svg-icons';
import { ITimelineBox, TenderSubCatName, TenderBoxMode } from 'src/models/procurements/Tender/types';
import { useTenderData } from 'src/models/procurements/Tender/hooks';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { InfoBox, TenderBox, TenderSubCat } from 'src/shared';
import { DateFormatTranslation, Translations } from 'src/lib/I18nService/types';

export const TimelineSubCat: FC = () => {
  const { t } = useTranslation();
  const { t: tenderT } = useTranslation(Translations.procurements);
  const { data, loading } = useTenderData();
  const subCat = useMemo(() => data?.generalCat[TenderSubCatName.Timeline], [data]);
  const rawDates = useMemo(() => subCat?.boxes ?? [], [subCat]);

  const indexOfToday = useMemo(() => rawDates.findIndex(({ title }) => title === 'today'), [rawDates]);
  const isExpired = useMemo(() => indexOfToday === rawDates.length - 1, [indexOfToday, rawDates.length]);
  const dates: ITimelineBox[] = useMemo(
    () =>
      rawDates.map((date, index) => ({
        box: date,
        isHidden: index !== 0 && index < indexOfToday
      })),
    [indexOfToday, rawDates]
  );

  const showExpandButton = dates.some(date => date.isHidden);

  const [showMore, setShowMore] = useState(true);
  const toggleVisible = useCallback(() => setShowMore(!showMore), [showMore]);
  const isEditable = !!data?.isEditable;

  return (
    <Skeleton active loading={loading}>
      {subCat && (
        <TenderSubCat subCat={subCat}>
          <div className={styles.timeline}>
            <div className={styles.timelineList}>
              {dates.map(({ box: date }, index) =>
                date.title === 'today' ? (
                  <React.Fragment key={date.id}>
                    {showExpandButton && (
                      <div className={classNames(styles.timelineExpandingButton, styles.date)}>
                        <div className={styles.dateInner}>
                          <span className={styles.point}>
                            <FontAwesomeIcon
                              icon={showMore ? faArrowsFromDottedLine : faArrowsToDottedLine}
                              className={styles.icon}
                            />
                          </span>
                          <div className={styles.content}>
                            <button onClick={toggleVisible} className={styles.expandingButton}>
                              {t(showMore ? 'DetailsPage.showAllDates' : 'DetailsPage.showLessDates')}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className={classNames(styles.today, styles.date, { [styles.isExpired]: isExpired })}>
                      <div className={styles.dateInner}>
                        <span className={styles.point}>
                          <FontAwesomeIcon icon={faClock} className={styles.icon} />
                        </span>
                        <div className={styles.content}>
                          <InfoBox value={t('Common.today')} className={styles.box} />
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ) : (
                  date.firstField && (
                    <div
                      className={classNames(styles.date, {
                        [styles.isPassed]: index < indexOfToday,
                        [styles.isHidden]: index !== 0 && index < indexOfToday && showMore
                      })}
                      key={date.id}
                    >
                      <div className={styles.dateInner}>
                        <span className={styles.point} />
                        <div className={styles.content}>
                          {/* FIXME: Hack - if deadline then display as datetime */}
                          <TenderBox
                            box={date}
                            title={tenderT(date.title)}
                            as={TenderBoxMode.Reverse}
                            className={styles.desc}
                            isInfo={date.fields.length > 1}
                            isFirstField
                            isEditable={isEditable}
                            config={{ date: { dateFormat: DateFormatTranslation.shortMonth } }}
                          />
                        </div>
                      </div>
                    </div>
                  )
                )
              )}
            </div>
          </div>
        </TenderSubCat>
      )}
    </Skeleton>
  );
};

export default TimelineSubCat;
